import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Chip, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  Tab,
  Tabs
} from '@mui/material';

import { 
  Add as AddIcon, 
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Build as BuildIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  Map as MapIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getApiUrl, API_ENDPOINTS, API_BASE_URL } from '../config/api';
import InfrastructureMaps from './InfrastructureMaps';

interface InfrastructureRequest {
  id: string;
  title: string;
  description: string;
  category: 'maintenance' | 'equipment' | 'facility' | 'safety' | 'technology' | 'other';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'accepted' | 'in_progress' | 'completed' | 'cancelled';
  requestedDate: string | null;
  location: string | null;
  department: string | null;
  adminNotes: string | null;
  requesterId: string;
  assignedTo: string | null;
  acceptedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  images: Array<{
    filename: string;
    originalName: string;
    path: string;
    size: number;
    mimetype: string;
    url: string;
  }> | null;
  requester: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  assignedUser: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
}

const Infrastructure: React.FC = () => {
  const { user, token, hasPermission } = useAuth();
  const { t } = useTranslation();
  const [requests, setRequests] = useState<InfrastructureRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [departments, setDepartments] = useState<{name: string, memberCount: number}[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<InfrastructureRequest | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [availableUsers, setAvailableUsers] = useState<Array<{id: string, firstName: string, lastName: string, email: string}>>([]);
  


  // Form states
  const [requestForm, setRequestForm] = useState({
    title: '',
    description: '',
    category: 'other' as const,
    priority: 'medium' as const,
    requestedDate: '',
    location: '',
    department: ''
  });

  const [statusForm, setStatusForm] = useState({
    status: 'pending' as 'pending' | 'accepted' | 'in_progress' | 'completed' | 'cancelled',
    adminNotes: ''
  });

  const [assignForm, setAssignForm] = useState({
    assignedTo: '',
    adminNotes: ''
  });

  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);

  useEffect(() => {
    fetchRequests();
    fetchDepartments();
    if (hasPermission('infrastructure.manage')) {
      fetchAvailableUsers();
    }
  }, [activeTab]);

  const fetchDepartments = async () => {
    try {
      const response = await fetch(getApiUrl('/projects/available/departments'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDepartments(data.departments);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      // Skip fetching for Maps tab (tab 4)
      if (activeTab === 4) {
        setLoading(false);
        return;
      }

      let endpoint = API_ENDPOINTS.INFRASTRUCTURE;
      if (activeTab === 1) {
        endpoint = API_ENDPOINTS.INFRASTRUCTURE_MY_REQUESTS;
      } else if (activeTab === 2) {
        endpoint = API_ENDPOINTS.INFRASTRUCTURE_ASSIGNED;
      } else if (activeTab === 3) {
        endpoint = API_ENDPOINTS.INFRASTRUCTURE;
      }

      const response = await fetch(getApiUrl(endpoint), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch infrastructure requests');
      }

      const data = await response.json();
      
      // Filter requests based on active tab
      let filteredRequests = data.requests;
      if (activeTab === 3) {
        // Completed tab - show only completed requests
        filteredRequests = data.requests.filter((request: InfrastructureRequest) => 
          request.status === 'completed'
        );
      } else if (activeTab === 0) {
        // All Requests tab - show all except completed
        filteredRequests = data.requests.filter((request: InfrastructureRequest) => 
          request.status !== 'completed'
        );
      }
      
      setRequests(filteredRequests);
    } catch (err) {
      console.error('Error fetching infrastructure requests:', err);
      setError('Failed to load infrastructure requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await fetch(getApiUrl('/projects/available/users'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableUsers(data.users);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const handleCreateRequest = async () => {
    try {
      setSubmitting(true);

      // Create FormData for multipart/form-data submission
      const formData = new FormData();
      formData.append('title', requestForm.title);
      formData.append('description', requestForm.description);
      formData.append('category', requestForm.category);
      formData.append('priority', requestForm.priority);

      // Only include optional fields if they have valid values
      if (requestForm.requestedDate && requestForm.requestedDate.trim() !== '') {
        formData.append('requestedDate', requestForm.requestedDate);
      }

      if (requestForm.location && requestForm.location.trim() !== '') {
        formData.append('location', requestForm.location);
      }

      if (requestForm.department && requestForm.department.trim() !== '') {
        formData.append('department', requestForm.department);
      }

      // Append images
      selectedImages.forEach((image, index) => {
        formData.append('images', image);
      });

      const response = await fetch(getApiUrl(API_ENDPOINTS.INFRASTRUCTURE), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type for FormData, let browser set it with boundary
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create infrastructure request');
      }

      await fetchRequests();
      setCreateDialogOpen(false);
      setRequestForm({
        title: '',
        description: '',
        category: 'other',
        priority: 'medium',
        requestedDate: '',
        location: '',
        department: ''
      });
      clearImages();
    } catch (err) {
      console.error('Error creating infrastructure request:', err);
      setError(err instanceof Error ? err.message : 'Failed to create infrastructure request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptRequest = async (requestId: string) => {
    try {
      setSubmitting(true);

      const response = await fetch(getApiUrl(API_ENDPOINTS.INFRASTRUCTURE_ACCEPT(requestId)), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          adminNotes: 'Request accepted by admin'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to accept request');
      }

      await fetchRequests();
    } catch (err) {
      console.error('Error accepting request:', err);
      setError(err instanceof Error ? err.message : 'Failed to accept request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignRequest = async () => {
    if (!selectedRequest || !assignForm.assignedTo) return;

    try {
      setSubmitting(true);

      const response = await fetch(getApiUrl(API_ENDPOINTS.INFRASTRUCTURE_ASSIGN(selectedRequest.id)), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          assignedTo: assignForm.assignedTo,
          adminNotes: assignForm.adminNotes || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to assign request');
      }

      await fetchRequests();
      setAssignDialogOpen(false);
      setAssignForm({ assignedTo: '', adminNotes: '' });
      setSelectedRequest(null);
    } catch (err) {
      console.error('Error assigning request:', err);
      setError(err instanceof Error ? err.message : 'Failed to assign request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedRequest) return;

    try {
      setSubmitting(true);

      const updateData: any = {
        status: statusForm.status
      };

      if (statusForm.adminNotes.trim() !== '') {
        updateData.adminNotes = statusForm.adminNotes;
      }

      const response = await fetch(getApiUrl(API_ENDPOINTS.INFRASTRUCTURE_STATUS(selectedRequest.id)), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update status');
      }

      await fetchRequests();
      setStatusDialogOpen(false);
      setStatusForm({ status: 'pending', adminNotes: '' });
      setSelectedRequest(null);
    } catch (err) {
      console.error('Error updating status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRequest = async (requestId: string) => {
    if (!window.confirm(t('infrastructure.confirmDelete', 'Are you sure you want to delete this infrastructure request? This action cannot be undone.'))) {
      return;
    }

    try {
      setSubmitting(true);

      const response = await fetch(getApiUrl(API_ENDPOINTS.INFRASTRUCTURE_DELETE(requestId)), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete request');
      }

      await fetchRequests();
    } catch (err) {
      console.error('Error deleting request:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete request');
    } finally {
      setSubmitting(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'accepted': return 'info';
      case 'in_progress': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'maintenance': return <BuildIcon />;
      case 'equipment': return <AssignmentIcon />;
      case 'facility': return <BuildIcon />;
      case 'safety': return <CheckCircleIcon />;
      case 'technology': return <EditIcon />;
      default: return <BuildIcon />;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'maintenance': return t('infrastructure.catMaintenance');
      case 'equipment': return t('infrastructure.catEquipment');
      case 'facility': return t('infrastructure.catFacility');
      case 'safety': return t('infrastructure.catSafety');
      case 'technology': return t('infrastructure.catTechnology');
      case 'other': return t('infrastructure.catOther');
      default: return category;
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'low': return t('infrastructure.priorityLow');
      case 'medium': return t('infrastructure.priorityMedium');
      case 'high': return t('infrastructure.priorityHigh');
      case 'urgent': return t('infrastructure.priorityUrgent');
      default: return priority;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return t('infrastructure.stPending');
      case 'accepted': return t('infrastructure.stAccepted');
      case 'in_progress': return t('infrastructure.stInProgress');
      case 'completed': return t('infrastructure.stCompleted');
      case 'cancelled': return t('infrastructure.stCancelled');
      default: return status;
    }
  };

  const openStatusDialog = (request: InfrastructureRequest) => {
    setSelectedRequest(request);
    setStatusForm({
      status: request.status,
      adminNotes: request.adminNotes || ''
    });
    setStatusDialogOpen(true);
  };

  const openAssignDialog = (request: InfrastructureRequest) => {
    setSelectedRequest(request);
    setAssignForm({
      assignedTo: request.assignedTo || '',
      adminNotes: request.adminNotes || ''
    });
    setAssignDialogOpen(true);
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    // Validate file count
    if (selectedImages.length + files.length > 5) {
      setError('Maximum 5 images allowed');
      return;
    }

    // Validate file types and sizes
    const validFiles = files.filter(file => {
      const isValidType = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'].includes(file.type);
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB
      
      if (!isValidType) {
        setError(`${file.name} is not a valid image type`);
        return false;
      }
      if (!isValidSize) {
        setError(`${file.name} is too large (max 5MB)`);
        return false;
      }
      
      return true;
    });

    if (validFiles.length > 0) {
      setSelectedImages(prev => [...prev, ...validFiles]);
      
      // Create preview URLs
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreviewUrls(prev => [...prev, e.target?.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const clearImages = () => {
    setSelectedImages([]);
    setImagePreviewUrls([]);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('infrastructure.title', 'Infrastructure Requests')}
        </Typography>
                 <Button
           variant="contained"
           startIcon={<AddIcon />}
           onClick={() => setCreateDialogOpen(true)}
         >
           {t('infrastructure.newRequest', 'New Request')}
         </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

             {/* Tabs */}
       <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
         <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
           <Tab label={t('infrastructure.tabAll', 'All Requests')} />
           <Tab label={t('infrastructure.tabMine', 'My Requests')} />
            {hasPermission('infrastructure.manage') && <Tab label={t('infrastructure.tabAssigned', 'Assigned to Me')} />}
           <Tab label={t('infrastructure.tabCompleted', 'Completed')} />
           <Tab label="Žemėlapiai" icon={<MapIcon />} />
         </Tabs>
       </Box>



      {/* Tab Content */}
      {activeTab === 4 ? (
        // Maps Tab
        <InfrastructureMaps />
      ) : (
        /* Requests List */
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 2 
        }}>
        {requests.map((request) => (
          <Card key={request.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                {/* Header with priority and category */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Chip
                    label={getPriorityLabel(request.priority)}
                    color={getPriorityColor(request.priority) as any}
                    size="small"
                  />
                  <Box display="flex" alignItems="center">
                    {getCategoryIcon(request.category)}
                    <Typography variant="caption" sx={{ ml: 0.5 }}>
                      {getCategoryLabel(request.category)}
                    </Typography>
                  </Box>
                </Box>

                {/* Title and Status */}
                <Typography variant="h6" gutterBottom>
                  {request.title}
                </Typography>
                <Chip
                  label={getStatusLabel(request.status)}
                  color={getStatusColor(request.status) as any}
                  size="small"
                  sx={{ mb: 2 }}
                />

                {/* Description */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {request.description.length > 150 
                    ? `${request.description.substring(0, 150)}...` 
                    : request.description}
                </Typography>

                {/* Location, Department and Date */}
                {request.location && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    📍 {request.location}
                  </Typography>
                )}
                {request.department && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    🏢 {request.department}
                  </Typography>
                )}
                {request.requestedDate && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    📅 {t('infrastructure.requestedOn', 'Requested')}: {new Date(request.requestedDate).toLocaleDateString()}
                  </Typography>
                )}

                {/* Requester and Assigned User */}
                <Box mt={2}>
                  <Typography variant="caption" color="text.secondary">
                    {t('infrastructure.requestedBy', 'Requested by')}: {request.requester.firstName} {request.requester.lastName}
                  </Typography>
                  {request.assignedUser && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {t('infrastructure.assignedTo', 'Assigned to')}: {request.assignedUser.firstName} {request.assignedUser.lastName}
                    </Typography>
                  )}
                </Box>

                {/* Images */}
                {request.images && request.images.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {t('infrastructure.attachedImages', 'Attached Images')} ({request.images.length})
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 1 }}>
                      {request.images.slice(0, 3).map((image, index) => (
                        <img
                          key={index}
                          src={image.url.startsWith('http') ? image.url : `${API_BASE_URL}${image.url}`}
                          alt={`Image ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '80px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(image.url.startsWith('http') ? image.url : `${API_BASE_URL}${image.url}`, '_blank')}
                          title={image.originalName}
                        />
                      ))}
                      {request.images.length > 3 && (
                        <Box
                          sx={{
                            width: '100%',
                            height: '80px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'grey.200',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                          onClick={() => {
                            // Show all images in a modal or new window
                            const imageUrls = request.images?.map(img => 
                              img.url.startsWith('http') ? img.url : `${API_BASE_URL}${img.url}`
                            ).join('\n');
                            if (imageUrls) {
                              window.open(imageUrls, '_blank');
                            }
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            +{request.images.length - 3}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}


              </CardContent>

              {/* Actions */}
              <Box p={2} pt={0}>
                {hasPermission('infrastructure.manage') && request.status === 'pending' && (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={() => handleAcceptRequest(request.id)}
                    disabled={submitting}
                    sx={{ mb: 1 }}
                  >
                    {t('infrastructure.accept', 'Accept Request')}
                  </Button>
                )}
                
                {hasPermission('infrastructure.manage') && (request.status === 'pending' || request.status === 'accepted') && (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    startIcon={<AssignmentIcon />}
                    onClick={() => openAssignDialog(request)}
                    disabled={submitting}
                    sx={{ mb: 1 }}
                  >
                    {t('infrastructure.assign', 'Assign to User')}
                  </Button>
                )}
                
                {((hasPermission('infrastructure.manage')) || 
                  (request.assignedTo === user?.id)) && 
                  request.status !== 'cancelled' && (
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => openStatusDialog(request)}
                    sx={{ mb: 1 }}
                  >
                    {t('infrastructure.updateStatus', 'Update Status')}
                  </Button>
                )}
                
                {hasPermission('infrastructure.manage') && (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteRequest(request.id)}
                    disabled={submitting}
                  >
                    {t('infrastructure.delete')}
                  </Button>
                )}
              </Box>
            </Card>
        ))}

        {requests.length === 0 && !loading && (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" color="text.secondary">
              {t('infrastructure.none', 'No infrastructure requests found')}
            </Typography>
          </Box>
        )}
      </Box>
      )}

      {/* Create Request Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('infrastructure.createTitle', 'Create Infrastructure Request')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('infrastructure.fieldTitle', 'Title')}
            fullWidth
            variant="outlined"
            value={requestForm.title}
            onChange={(e) => setRequestForm({ ...requestForm, title: e.target.value })}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label={t('infrastructure.fieldDescription', 'Description')}
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={requestForm.description}
            onChange={(e) => setRequestForm({ ...requestForm, description: e.target.value })}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel>{t('infrastructure.fieldCategory', 'Category')}</InputLabel>
              <Select
                value={requestForm.category}
                label={t('infrastructure.fieldCategory', 'Category')}
                onChange={(e) => setRequestForm({ ...requestForm, category: e.target.value as any })}
              >
                <MenuItem value="maintenance">{t('infrastructure.catMaintenance', 'Maintenance')}</MenuItem>
                <MenuItem value="equipment">{t('infrastructure.catEquipment', 'Equipment')}</MenuItem>
                <MenuItem value="facility">{t('infrastructure.catFacility', 'Facility')}</MenuItem>
                <MenuItem value="safety">{t('infrastructure.catSafety', 'Safety')}</MenuItem>
                <MenuItem value="technology">{t('infrastructure.catTechnology', 'Technology')}</MenuItem>
                <MenuItem value="other">{t('infrastructure.catOther', 'Other')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('infrastructure.fieldPriority', 'Priority')}</InputLabel>
              <Select
                value={requestForm.priority}
                label={t('infrastructure.fieldPriority', 'Priority')}
                onChange={(e) => setRequestForm({ ...requestForm, priority: e.target.value as any })}
              >
                <MenuItem value="low">{t('infrastructure.priorityLow', 'Low')}</MenuItem>
                <MenuItem value="medium">{t('infrastructure.priorityMedium', 'Medium')}</MenuItem>
                <MenuItem value="high">{t('infrastructure.priorityHigh', 'High')}</MenuItem>
                <MenuItem value="urgent">{t('infrastructure.priorityUrgent', 'Urgent')}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TextField
            margin="dense"
            label={t('infrastructure.fieldLocation', 'Location')}
            fullWidth
            variant="outlined"
            value={requestForm.location}
            onChange={(e) => setRequestForm({ ...requestForm, location: e.target.value })}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('infrastructure.fieldDepartment', 'Department')}</InputLabel>
            <Select
              value={requestForm.department}
              label={t('infrastructure.fieldDepartment', 'Department')}
              onChange={(e) => setRequestForm({ ...requestForm, department: e.target.value })}
            >
              <MenuItem value="">
                <em>{t('infrastructure.notSpecified', 'Not specified')}</em>
              </MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept.name} value={dept.name}>
                  {dept.name} ({dept.memberCount} {t('infrastructure.members', 'members')})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            margin="dense"
            label={t('infrastructure.fieldRequestedDate', 'Requested Completion Date')}
            type="date"
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            value={requestForm.requestedDate}
            onChange={(e) => setRequestForm({ ...requestForm, requestedDate: e.target.value })}
          />

          {/* Image Upload Section */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('infrastructure.images', 'Images')} ({selectedImages.length}/5)
            </Typography>
            
            {/* File Input */}
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="image-upload"
              multiple
              type="file"
              onChange={handleImageSelect}
            />
            <label htmlFor="image-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<AddIcon />}
                disabled={selectedImages.length >= 5}
                sx={{ mb: 2 }}
              >
                {t('infrastructure.addImages', 'Add Images')}
              </Button>
            </label>

            {/* Image Previews */}
            {imagePreviewUrls.length > 0 && (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 1, mb: 2 }}>
                {imagePreviewUrls.map((url, index) => (
                  <Box key={index} sx={{ position: 'relative' }}>
                    <img
                      src={url}
                      alt={`Preview ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '120px',
                        objectFit: 'cover',
                        borderRadius: '4px'
                      }}
                    />
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.9)'
                        }
                      }}
                      onClick={() => removeImage(index)}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {/* Clear All Images Button */}
            {selectedImages.length > 0 && (
              <Button
                variant="text"
                color="error"
                size="small"
                onClick={clearImages}
                sx={{ mb: 1 }}
              >
                {t('infrastructure.clearImages', 'Clear All Images')}
              </Button>
            )}

            <Typography variant="caption" color="text.secondary" display="block">
              {t('infrastructure.imageHelp', 'Upload up to 5 images (JPEG, PNG, GIF, WebP). Max size: 5MB per image.')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button 
            onClick={handleCreateRequest} 
            variant="contained"
            disabled={!requestForm.title.trim() || !requestForm.description.trim() || submitting}
          >
            {submitting ? <CircularProgress size={20} /> : t('infrastructure.createAction', 'Create Request')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('infrastructure.updateTitle', 'Update Request Status')}</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                {selectedRequest.title}
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t('infrastructure.fieldStatus', 'Status')}</InputLabel>
                <Select
                  value={statusForm.status}
                  label={t('infrastructure.fieldStatus', 'Status')}
                  onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value as any })}
                >
                  <MenuItem value="pending">{t('infrastructure.stPending', 'Pending')}</MenuItem>
                  <MenuItem value="accepted">{t('infrastructure.stAccepted', 'Accepted')}</MenuItem>
                  <MenuItem value="in_progress">{t('infrastructure.stInProgress', 'In Progress')}</MenuItem>
                  <MenuItem value="completed">{t('infrastructure.stCompleted', 'Completed')}</MenuItem>
                  <MenuItem value="cancelled">{t('infrastructure.stCancelled', 'Cancelled')}</MenuItem>
                </Select>
              </FormControl>

              <TextField
                margin="dense"
                label={t('infrastructure.fieldAdminNotes', 'Admin Notes')}
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={statusForm.adminNotes}
                onChange={(e) => setStatusForm({ ...statusForm, adminNotes: e.target.value })}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button 
            onClick={handleUpdateStatus} 
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : t('infrastructure.updateAction', 'Update Status')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assignment Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('infrastructure.assignTitle', 'Assign Request to User')}</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                {selectedRequest.title}
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t('infrastructure.fieldAssignTo', 'Assign to User')}</InputLabel>
                <Select
                  value={assignForm.assignedTo}
                  label={t('infrastructure.fieldAssignTo', 'Assign to User')}
                  onChange={(e) => setAssignForm({ ...assignForm, assignedTo: e.target.value })}
                >
                  <MenuItem value="">
                    <em>{t('infrastructure.selectUser', 'Select a user...')}</em>
                  </MenuItem>
                  {availableUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} ({user.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                margin="dense"
                label={t('infrastructure.fieldAdminNotes', 'Admin Notes')}
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={assignForm.adminNotes}
                onChange={(e) => setAssignForm({ ...assignForm, adminNotes: e.target.value })}
                placeholder={t('infrastructure.notesPlaceholder', 'Optional notes about this assignment...')}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button 
            onClick={handleAssignRequest} 
            variant="contained"
            disabled={!assignForm.assignedTo || submitting}
          >
            {submitting ? <CircularProgress size={20} /> : t('infrastructure.assignAction', 'Assign Request')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Infrastructure;